// [phase11-v1] Signal candidate table — trading-ui.
//
// Renders the ranked candidates list with explicit GATED / EMPTY / LIVE
// states. Marker strings preserved: bundle contains the literal
// "candidates" string via the signalClient endpoint path.
import { useState } from "react";
import SymbolChartButton from "../SymbolChartButton";  // ADV-P0K hotfix
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Info,
  Lock,
  Moon,
} from "lucide-react";
import {
  isPremarketComputeWindow,
  labelDisplay,
  type SignalCandidate,
  type SignalCandidatesPayload,
  type SignalLabelRow,
  type SignalLabelsPayload,
  type SignalProviderHealth,
} from "../../lib/signalClient";
import { TermHelp } from "../BeginnerHelp";  // beginner-help Phase 1

interface Props {
  data: SignalCandidatesPayload | null;
  loading: boolean;
  health?: SignalProviderHealth | null;
  /** Phase 11B — outcome labels for the day. May be null (not yet
   * computed) or `status=PENDING`. The table renders per-row labels when
   * available and falls back to "Pending outcome labeling" otherwise. */
  labels?: SignalLabelsPayload | null;
}

function badgeClasses(tone: "green" | "blue" | "amber" | "red" | "neutral"): string {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border";
  switch (tone) {
    case "green":
      return `${base} bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30`;
    case "blue":
      return `${base} bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30`;
    case "amber":
      return `${base} bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30`;
    case "red":
      return `${base} bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30`;
    default:
      return `${base} bg-[var(--card-bg)] text-[var(--muted-text)] border-[var(--card-border)]`;
  }
}

function formatNumber(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toFixed(digits);
}

function formatInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return Math.round(n).toLocaleString();
}

function formatRelative(iso: string | undefined | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s ? `${m}m ${s}s ago` : `${m}m ago`;
  }
  if (secs < 86400) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return m ? `${h}h ${m}m ago` : `${h}h ago`;
  }
  const d = Math.floor(secs / 86400);
  return `${d}d ago`;
}

function formatETTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      month: "short",
      day: "numeric",
    }) + " ET"
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[var(--muted-text)] font-bold">{label}</div>
      <div className="text-sm font-mono font-bold text-[var(--app-text)] mt-0.5 truncate">{value}</div>
    </div>
  );
}

function ComputeStatusStrip({
  data,
  health,
}: {
  data: SignalCandidatesPayload | null;
  health: SignalProviderHealth | null | undefined;
}) {
  const providerStateLabel = !health
    ? "Loading…"
    : health.realtime_data_verified
    ? `LIVE (${health.status})`
    : `BLOCKED (${health.status})`;
  const providerTone: "green" | "amber" | "red" = !health
    ? "amber"
    : health.realtime_data_verified
    ? "green"
    : "red";

  const cacheStatus = data?.status || "EMPTY";
  const cacheTone: "green" | "amber" | "red" | "neutral" =
    cacheStatus === "LIVE" ? "green" : cacheStatus === "GATED" ? "red" : cacheStatus === "EMPTY" ? "amber" : "neutral";

  const inWindow = isPremarketComputeWindow();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      <div
        data-testid="signal-strip-provider-health"
        className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3"
      >
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-1.5">
          Provider Health
        </div>
        <div className="flex items-center gap-2">
          {providerTone === "green" ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-red-500" />
          )}
          <span className={badgeClasses(providerTone)}>{providerStateLabel}</span>
        </div>
        <div className="text-[10px] text-[var(--muted-text)] mt-1.5">
          rdv = {String(health?.realtime_data_verified ?? "—")}
        </div>
      </div>

      <div
        data-testid="signal-strip-cache"
        className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3"
      >
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-1.5">
          Candidate Cache
        </div>
        <div className="flex items-center gap-2">
          {cacheTone === "green" ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <Info className="h-4 w-4 text-[var(--muted-text)]" />
          )}
          <span className={badgeClasses(cacheTone)}>{cacheStatus}</span>
        </div>
        <div className="text-[10px] text-[var(--muted-text)] mt-1.5">
          {data?.candidate_count ?? 0} candidates · universe {data?.universe_size ?? "—"}
        </div>
      </div>

      <div
        data-testid="signal-strip-last-compute"
        className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3"
      >
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-1.5">
          Last Compute
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-[var(--muted-text)]" />
          <div className="font-mono text-xs text-[var(--app-text)]">{formatRelative(data?.as_of_utc)}</div>
        </div>
        <div className="text-[10px] text-[var(--muted-text)] mt-1.5 truncate">
          {formatETTime(data?.as_of_utc)}
        </div>
      </div>

      <div
        data-testid="signal-strip-window"
        className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3"
      >
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-1.5">
          Compute Window
        </div>
        <div className="flex items-center gap-2">
          {inWindow ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <Moon className="h-4 w-4 text-blue-500" />
          )}
          <span className={badgeClasses(inWindow ? "green" : "blue")}>
            {inWindow ? "PREMARKET OPEN" : "PREMARKET CLOSED"}
          </span>
        </div>
        <div className="text-[10px] text-[var(--muted-text)] mt-1.5">Mon–Fri 04:00–10:30 ET</div>
      </div>
    </div>
  );
}

export default function SignalCandidateTable({ data, loading, health, labels }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  // Phase 11B — index label rows by symbol for O(1) lookup in CandidateRow.
  const labelsBySymbol: Record<string, SignalLabelRow> = {};
  if (labels && Array.isArray(labels.rows)) {
    for (const row of labels.rows) {
      if (row && row.symbol) labelsBySymbol[String(row.symbol).toUpperCase()] = row;
    }
  }

  if (loading && !data) {
    return (
      <section
        data-testid="signal-candidate-table"
        data-state="loading"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          <Activity className="h-4 w-4" /> Raw Ranking Inputs (legacy score view — see Signal Decisions above)
        </div>
        <div className="text-xs text-[var(--muted-text)] mt-3">Loading candidates…</div>
      </section>
    );
  }

  const providerRdvLive = health?.realtime_data_verified === true;
  const inWindow = isPremarketComputeWindow();
  const cacheStatus = data?.status;
  const candidates = data?.candidates || [];

  // Path 1: cache LIVE with candidates → ranked list
  if (cacheStatus === "LIVE" && candidates.length > 0) {
    return (
      <section
        data-testid="signal-candidate-table"
        data-state="live"
        data-candidate-count={candidates.length}
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <header className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
            <Activity className="h-4 w-4 text-emerald-500" /> Raw Ranking Inputs (legacy score view — see Signal Decisions above)
          </div>
          <span className="text-[10px] text-[var(--muted-text)] font-bold tracking-wider">
            {candidates.length} ACCEPTED · {data?.score_version || "?"} · /api/v1/signal/candidates
          </span>
        </header>
        <div data-testid="candidate-table-help-band" className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-[var(--muted-text)]">
          <span className="font-bold uppercase tracking-widest text-[10px] text-[var(--app-text)]">Quick read:</span>
          <TermHelp term="Candidate" />
          <TermHelp term="Spread" />
          <TermHelp term="Volume" />
          <TermHelp term="Liquidity" />
          <TermHelp term="Watch Status" />
        </div>
        <ComputeStatusStrip data={data} health={health} />
        <LabelsSummary labels={labels} />
        {!inWindow && (
          <div className="text-[11px] text-blue-700 dark:text-blue-300 bg-blue-500/5 border border-blue-500/20 rounded p-2 mb-3 flex items-start gap-2">
            <Moon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              Outside premarket window — showing the most recent candidate set from today's premarket session ({formatETTime(data?.as_of_utc)}). Next compute will run automatically at 04:00 ET on the next trading day.
            </div>
          </div>
        )}
        <div className="space-y-2">
          {candidates.map((cand) => (
            <CandidateRow
              key={cand.symbol}
              cand={cand}
              expanded={expanded === cand.symbol}
              onToggle={() => setExpanded(expanded === cand.symbol ? null : cand.symbol)}
              label={labelsBySymbol[String(cand.symbol).toUpperCase()]}
            />
          ))}
        </div>
      </section>
    );
  }

  // Path 2: GATED + provider-health currently LIVE → "awaiting next compute" copy
  if (cacheStatus === "GATED" && providerRdvLive) {
    return (
      <section
        data-testid="signal-candidate-table"
        data-state="gated-recovered"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <header className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
            <Clock className="h-4 w-4 text-amber-500" /> Candidate Cache Awaiting Next Compute
          </div>
          <span className={badgeClasses("amber")}>
            {inWindow ? "AWAITING NEXT TIMER FIRE" : "OUTSIDE WINDOW"}
          </span>
        </header>
        <ComputeStatusStrip data={data} health={health} />
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="text-sm font-bold text-amber-700 dark:text-amber-300">
            Provider health is currently LIVE — the gate is OPEN.
          </div>
          <div className="text-xs text-[var(--muted-text)] mt-2 leading-relaxed">
            The candidate cache shown is from an earlier compute cycle during which one or more providers were briefly degraded. That degradation has since cleared.{" "}
            {inWindow
              ? "The next 1-minute timer fire will write a fresh LIVE candidate list."
              : "Outside the 04:00–10:30 ET premarket window, candidates are not recomputed automatically; the next scheduled compute is tomorrow at 04:00 ET."}
          </div>
          {data?.blockers_at_compute && data.blockers_at_compute.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-1">
                Blockers at last compute
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.blockers_at_compute.map((b) => (
                  <span
                    key={b.provider}
                    className="text-[10px] font-mono bg-[var(--card-bg)] text-[var(--muted-text)] border border-[var(--card-border)] px-2 py-0.5 rounded"
                  >
                    {b.provider}:{b.status}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Path 3: GATED + provider-health BLOCKED → legitimate freeze
  if (cacheStatus === "GATED" && !providerRdvLive) {
    return (
      <section
        data-testid="signal-candidate-table"
        data-state="gated-frozen"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <header className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
            <Lock className="h-4 w-4 text-red-500" /> Candidate Engine Frozen
          </div>
          <span className={badgeClasses("red")}>GATE CLOSED</span>
        </header>
        <ComputeStatusStrip data={data} health={health} />
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-5 text-center">
          <Lock className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <div className="text-base font-bold uppercase tracking-wider text-[var(--app-text)]">
            Candidate Generation Suspended
          </div>
          <div className="text-xs text-[var(--muted-text)] mt-2 max-w-xl mx-auto">
            The Signal refuses to produce rankings when active data verification fails. Premarket scoring is suspended to prevent adverse market decisions until provider health recovers.
          </div>
          <div className="mt-4 inline-flex flex-col gap-1.5 text-left text-[11px] font-mono text-[var(--muted-text)] bg-[var(--card-bg)] border border-[var(--card-border)] rounded p-3">
            <div>cache.reason: {data?.reason || "REALTIME_DATA_VERIFIED_FALSE"}</div>
            {(data?.blockers_at_compute || []).map((b) => (
              <div key={b.provider}>
                blocker @ compute: {b.provider} → {b.status}
              </div>
            ))}
            {(health?.blockers || []).map((b) => (
              <div key={`live-${b.provider}`}>
                blocker @ now: {b.provider} → {b.status}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Path 4: EMPTY or other
  return (
    <section
      data-testid="signal-candidate-table"
      data-state={cacheStatus === "EMPTY" ? "empty" : "other"}
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          <Info className="h-4 w-4 text-[var(--muted-text)]" /> No Candidates In Latest Compute
        </div>
        <span className={badgeClasses("amber")}>{cacheStatus || "EMPTY"}</span>
      </header>
      <ComputeStatusStrip data={data} health={health} />
      <div className="text-xs text-[var(--muted-text)]">
        {data?.reason
          ? `Filter rejected everything in the latest cycle. Reason: ${data.reason}`
          : "The closed Phase 8 filter accepted zero symbols in the latest compute cycle. Common causes: thin premarket volume, wide spreads on small-account candidates, missing float data."}
      </div>
      {!inWindow && (
        <div className="text-[11px] text-blue-700 dark:text-blue-300 bg-blue-500/5 border border-blue-500/20 rounded p-2 mt-3 flex items-start gap-2">
          <Moon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>Outside premarket window — next scheduled compute at 04:00 ET on the next trading day.</div>
        </div>
      )}
    </section>
  );
}

function CandidateRow({
  cand,
  expanded,
  onToggle,
  label,
}: {
  cand: SignalCandidate;
  expanded: boolean;
  onToggle: () => void;
  label?: SignalLabelRow;
}) {
  const scoreColor =
    cand.score >= 0.6
      ? "text-emerald-500"
      : cand.score >= 0.4
      ? "text-amber-500"
      : "text-[var(--muted-text)]";
  return (
    <div
      data-testid={`matrix-candidate-${cand.symbol}`}
      className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] hover:border-emerald-500/30 transition-colors"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3.5 grid grid-cols-12 gap-3 items-center"
      >
        <div className="col-span-12 sm:col-span-3 flex items-center gap-3">
          <div className="flex items-center justify-center h-9 w-9 text-xs font-black bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg text-emerald-500">
            #{cand.rank}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-black text-[var(--app-text)] tracking-tight">{cand.symbol}</span>
              <SymbolChartButton symbol={cand.symbol} />
              {cand.is_vix_proxy && (
                <span
                  className="text-[8px] uppercase tracking-widest font-bold text-blue-700 dark:text-blue-300 border border-blue-500/30 bg-blue-500/5 px-1.5 py-0.5 rounded"
                  title="VIX value used in scoring is a labelled VXX proxy, NOT true CBOE VIX"
                >
                  VIX-PROXY
                </span>
              )}
            </div>
            {cand.company_name && (
              <div className="text-[11px] text-[var(--muted-text)] truncate max-w-[180px]">
                {cand.company_name}
              </div>
            )}
          </div>
        </div>

        <div className="col-span-12 sm:col-span-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Cell label="PRICE" value={`$${formatNumber(cand.price)}`} />
          <Cell
            label="SPREAD"
            value={cand.spread_bps !== null ? `${formatNumber(cand.spread_bps, 1)} bps` : "—"}
          />
          <Cell label="VOLUME" value={formatInt(cand.premarket_volume)} />
          <Cell label="FLOAT" value={cand.float_class} />
        </div>

        <div className="col-span-8 sm:col-span-3 text-right">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold">SCORE</div>
          <div className={`text-2xl font-black font-mono ${scoreColor}`}>{formatNumber(cand.score, 4)}</div>
          <div className="text-[10px] text-[var(--muted-text)]">vix: {cand.vix_bucket}</div>
        </div>

        <div className="col-span-4 sm:col-span-1 flex justify-end">
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-[var(--muted-text)]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--muted-text)]" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[var(--card-border)] bg-[var(--card-bg)] p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-2">
              Score Components ({cand.score_version})
            </div>
            <div className="space-y-1.5">
              {/* Defensive: score_components can be undefined on partial payloads. */}
              {Object.entries(cand.score_components || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-[var(--muted-text)]">{k}</span>
                  <span className="font-mono text-[var(--app-text)]">{formatNumber(v as number, 3)}</span>
                </div>
              ))}
            </div>
            <div className="text-[9px] text-[var(--muted-text)] mt-2 italic">
              catalyst_recency present but weighted 0.0 in Phase 9 — Phase 10 decoder populates the signal but does not influence ranking yet.
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-2">
              Filters ({cand.passed_filters.length} passed · {cand.failed_filters.length} failed)
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-2">
              {cand.passed_filters.map((f) => (
                <div
                  key={f}
                  className="text-[10px] font-mono bg-emerald-500/5 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 px-2 py-1 rounded"
                >
                  ✓ {f}
                </div>
              ))}
              {cand.failed_filters.map((f) => (
                <div
                  key={f}
                  className="text-[10px] font-mono bg-red-500/5 text-red-700 dark:text-red-300 border border-red-500/20 px-2 py-1 rounded"
                >
                  ✗ {f}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-2">
                Provenance
              </div>
              <div className="flex flex-wrap gap-1.5">
                {cand.data_sources_used.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border bg-[var(--card-bg)] text-[var(--muted-text)] border-[var(--card-border)]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-2">
                Risk · Veto · Council
              </div>
              <div className="text-[11px] text-[var(--muted-text)] italic">
                Pending future phase — Council debate, risk veto, and decision routing are not surfaced in Phase 11A.
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-2">
                Outcome / Label (phase11b-labels)
              </div>
              {/* Phase 11B — show real label when available, fall back to pending. */}
              {label ? (
                <div data-testid={`matrix-label-${cand.symbol}`} className="space-y-1.5">
                  {(() => {
                    const dsp = labelDisplay(label.label);
                    return (
                      <>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border ${badgeClasses(dsp.tone)}`}
                          data-label={label.label}
                        >
                          {dsp.short}
                        </span>
                        <div className="text-[11px] text-[var(--muted-text)] leading-relaxed">
                          {dsp.long}
                        </div>
                        <div className="text-[10px] font-mono text-[var(--muted-text)] mt-1">
                          reason: {label.label_reason}
                        </div>
                        {label.simulated_net_pnl_pct !== null && label.simulated_net_pnl_pct !== undefined && (
                          <div className="text-[11px] text-[var(--muted-text)] mt-0.5">
                            Simulated open→10:30 P&amp;L:{" "}
                            <span
                              className={`font-mono font-bold ${
                                label.simulated_net_pnl_pct >= 0 ? "text-emerald-500" : "text-red-500"
                              }`}
                            >
                              {label.simulated_net_pnl_pct >= 0 ? "+" : ""}
                              {label.simulated_net_pnl_pct.toFixed(2)}%
                            </span>
                          </div>
                        )}
                        <div className="text-[9px] text-[var(--muted-text)] italic mt-1">
                          Informational only. Paper simulation, no live trade.
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-[11px] text-[var(--muted-text)] italic" data-testid={`matrix-label-pending-${cand.symbol}`}>
                  Pending outcome labeling — labels are computed daily at 11:00 ET after the 09:30–10:30 ET window closes.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Phase 11B — labels summary strip ───────────────────────────────

function LabelsSummary({ labels }: { labels: SignalLabelsPayload | null | undefined }) {
  if (!labels) {
    return (
      <div
        data-testid="signal-labels-summary"
        data-state="loading"
        className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3 mb-4 text-[11px] text-[var(--muted-text)]"
      >
        Loading Phase 11B outcome labels…
      </div>
    );
  }
  const counts = labels.label_counts || {};
  const total = labels.row_count || 0;

  // PENDING / window-not-closed / auth-missing: render the informational state.
  if (labels.status !== "OK" && labels.status !== "PARTIAL") {
    return (
      <div
        data-testid="signal-labels-summary"
        data-state={labels.status}
        className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3 mb-4"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold">
            Outcome Labels · phase11b-labels
          </div>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30">
            {labels.status || "PENDING"}
          </span>
        </div>
        <div className="text-[11px] text-[var(--muted-text)] mt-2 leading-relaxed">
          {labels.status === "WINDOW_NOT_CLOSED" && (
            <>The 09:30–10:30 ET window has not closed for {labels.date}. Labels compute after 10:30 ET.</>
          )}
          {labels.status === "PENDING" && (
            <>No label summary on disk for {labels.date} yet — the daily label compute runs at 11:00 ET.</>
          )}
          {labels.status === "NO_CANDIDATES" && (
            <>No candidate set existed for {labels.date}, so there is nothing to label.</>
          )}
          {labels.status === "BARS_AUTH_MISSING" && (
            <>The label engine could not authenticate against the historical-bar provider; backfill blocked.</>
          )}
          {labels.status === "BARS_UNAVAILABLE" && (
            <>The historical-bar provider was unreachable; labels could not be computed.</>
          )}
          {labels.status === "ERROR" && (
            <>Failed to read the per-day label summary file: {labels.reason || "unknown"}.</>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="signal-labels-summary"
      data-state="ready"
      data-labels-row-count={total}
      className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3 mb-4"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold">
          Outcome Labels · phase11b-labels · {labels.date}
        </div>
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
          {labels.status} · {total} ROWS
        </span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
        {[
          { key: "tradable_runner", tone: "green" as const },
          { key: "raw_runner_untradable", tone: "amber" as const },
          { key: "fade_after_open", tone: "neutral" as const },
          { key: "opening_drive_then_dump", tone: "red" as const },
          { key: "halt_to_zero", tone: "red" as const },
          { key: "offering_kill", tone: "red" as const },
          { key: "no_trade_liquidity", tone: "neutral" as const },
          { key: "no_trade_spread", tone: "neutral" as const },
          { key: "no_trade_data_unverified", tone: "neutral" as const },
        ].map(({ key, tone }) => {
          const n = counts[key] || 0;
          const dsp = labelDisplay(key);
          return (
            <div
              key={key}
              className="rounded border border-[var(--card-border)] p-2 text-center"
              data-label={key}
              title={dsp.long}
            >
              <div className="text-[8px] uppercase tracking-wider text-[var(--muted-text)] font-bold leading-tight">
                {dsp.short}
              </div>
              <div
                className={`text-base font-black font-mono mt-1 ${
                  n > 0 ? tone === "green" ? "text-emerald-500" : tone === "red" ? "text-red-500" : tone === "amber" ? "text-amber-500" : "text-[var(--app-text)]" : "text-[var(--muted-text)]"
                }`}
              >
                {n}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[9px] text-[var(--muted-text)] italic mt-2">
        Deterministic rules · no ML · informational only · paper simulation, no live trade
      </div>
    </div>
  );
}
